/**
 * Pure presentation helpers for artifacts (f15.1). No rule lives here: the
 * format, the version number and every permission come from the server.
 */

const DATE = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** `PDF` or `HTML`, from the sniffed content type the server stored. */
export function typeLabel(contentType: string): string {
  return contentType === 'application/pdf' ? 'PDF' : 'HTML';
}

export function formatDate(value: string | null | undefined): string {
  return value ? DATE.format(new Date(value)) : '—';
}

export function formatDateTime(value: string | null | undefined): string {
  return value ? DATE_TIME.format(new Date(value)) : '—';
}

/** Human-readable size: `2.4 MB`, `812 KB`, `640 B`. */
export function formatSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`;
  return `${bytes} B`;
}

/** The first and last four characters of a SHA-256: `9f21…c4a8`. */
export function shortHash(sha256: string): string {
  return sha256.length > 8 ? `${sha256.slice(0, 4)}…${sha256.slice(-4)}` : sha256;
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

const LEVEL_LABELS: Record<string, string> = {
  BUSINESS_UNIT_GROUP: 'Business Unit Group',
  BUSINESS_UNIT: 'Business Unit',
  CLIENT: 'Client',
  ENGAGEMENT: 'Engagement',
};

export function levelLabel(nodeType: string): string {
  return LEVEL_LABELS[nodeType] ?? nodeType;
}

/**
 * Where the owning node's screen is. Engagements have no screen of their own
 * (they are a tab inside their client), so they have no link here.
 */
export function ownerPath(owner: { type: string; id: string }): string | null {
  switch (owner.type) {
    case 'BUSINESS_UNIT_GROUP':
      return `/business-unit-groups/${encodeURIComponent(owner.id)}`;
    case 'BUSINESS_UNIT':
      return `/business-units/${encodeURIComponent(owner.id)}`;
    case 'CLIENT':
      return `/clients/${encodeURIComponent(owner.id)}?tab=artifacts`;
    default:
      return null;
  }
}

/** A download name for the browser: the server's attachment name is not readable cross-origin. */
export function downloadName(title: string, versionNumber: number, contentType: string): string {
  const slug =
    title
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'artifact';
  return `${slug}-v${versionNumber}.${contentType === 'application/pdf' ? 'pdf' : 'html'}`;
}

/** Hand a blob to the browser as a file download. */
export function saveBlob(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(href);
}

/* --- f15.2: the top-level list, filtered and paged in the browser --------- */

export type LevelFilter = '' | 'BUSINESS_UNIT_GROUP' | 'BUSINESS_UNIT' | 'CLIENT' | 'ENGAGEMENT';
export type StateFilter = '' | 'PUBLISHED' | 'PRIVATE';

export interface ArtifactFilters {
  search: string;
  level: LevelFilter;
  state: StateFilter;
}

export const NO_FILTERS: ArtifactFilters = { search: '', level: '', state: '' };

/** The prototype's 8 rows per page. */
export const ARTIFACT_PAGE_SIZE = 8;

interface ListedArtifact {
  title: string;
  description?: string | null;
  published_state: string;
  owner: { type: string; code: string; name: string };
}

/** Title, description, owner name and owner code, case-insensitively; all three filters combine. */
export function filterArtifacts<T extends ListedArtifact>(rows: readonly T[], filters: ArtifactFilters): T[] {
  const needle = filters.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (filters.level && row.owner.type !== filters.level) return false;
    if (filters.state && row.published_state !== filters.state) return false;
    if (!needle) return true;
    return [row.title, row.description ?? '', row.owner.name, row.owner.code].some((text) =>
      text.toLowerCase().includes(needle),
    );
  });
}

export interface ArtifactPage<T> {
  rows: T[];
  page: number;
  pageCount: number;
  from: number;
  to: number;
  total: number;
}

export function artifactPageOf<T>(rows: readonly T[], page: number, size = ARTIFACT_PAGE_SIZE): ArtifactPage<T> {
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, page), pageCount);
  const start = (current - 1) * size;
  return {
    rows: rows.slice(start, start + size),
    page: current,
    pageCount,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + size, total),
    total,
  };
}

/**
 * Where a row's "Hangs off" link goes: the owning node's screen, or -- for an
 * engagement, which has no screen of its own -- its client's detail.
 */
export function hangsOffPath(owner: { type: string; id: string; parent?: { id: string } | null }): string | null {
  if (owner.type === 'ENGAGEMENT') {
    return owner.parent ? `/clients/${encodeURIComponent(owner.parent.id)}?tab=engagements` : null;
  }
  if (owner.type === 'CLIENT') return `/clients/${encodeURIComponent(owner.id)}`;
  return ownerPath(owner);
}
